import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { LogOut, User, Building, Hotel, ShoppingCart, ArrowLeft, Calendar, MapPin, Clock, X, CreditCard, FileText, Download, Mail, Phone, Star, Filter, ChevronDown, Bed, Users, Search, DollarSign, TrendingUp, Wallet, Clock as ClockIcon, Receipt, Eye, Key, Settings, Ticket } from 'lucide-react'
import commissionService from '../services/commissionService'
import travelAgentService from '../services/travelAgentService'
import LoadingSpinner from '../components/LoadingSpinner'
import MainLayout from '../components/Layout/MainLayout'
import ExperienceCommissionBookingsModal from '../components/ExperienceCommissionBookingsModal'
import { ACCOUNT_PAGES_HERO_IMAGE_URL } from '../constants/accountPagesHeroImage'
import { getHotelStaySourcePresentation } from '../utils/staySourceLabel'

const UserBookingPage = () => {
  const [commissions, setCommissions] = useState([])
  const [filteredData, setFilteredData] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedCommission, setSelectedCommission] = useState(null)
  const [commissionType, setCommissionType] = useState('hotel')
  const [currentMonthStats, setCurrentMonthStats] = useState({
    totalBookingValue: 0,
    totalBaseAmount: 0,
    totalCommission: 0,
    paidCommission: 0,
    pendingCommission: 0
  })
  const [showPayoutsModal, setShowPayoutsModal] = useState(false)

  const [showBookingsModal, setShowBookingsModal] = useState(false)
  const [showExperienceBookingsModal, setShowExperienceBookingsModal] = useState(false)
  const [pagination, setPagination] = useState({
    totalRecords: 0,
    currentPage: 1,
    totalPages: 1,
    limit: 6
  })
  const itemsPerPage = 6

  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    loadData()
  }, [currentPage, commissionType])

  useEffect(() => {
    fetchCurrentMonthTotal()
  }, [commissionType])

  useEffect(() => {
    // Ensure data is always an array before sorting
    const sortedData = (Array.isArray(commissions) ? [...commissions] : [])
      .sort((a, b) => new Date(b.commissionMonth) - new Date(a.commissionMonth))
    setFilteredData(sortedData)
  }, [commissions])

  const loadData = async () => {
    try {
      setIsLoading(true)
      let result
      if (commissionType === 'hotel') {
        result = await commissionService.getAgentCommission(currentPage, itemsPerPage)
      } else {
        result = await commissionService.getExperienceCommission(currentPage, itemsPerPage)
      }
      const commissionData = result?.data?.data || []
      const paginationData = result?.data?.pagination || {}

      setCommissions(Array.isArray(commissionData) ? commissionData : [])
      setPagination({
        totalRecords: paginationData.totalRecords || 0,
        currentPage: paginationData.currentPage || currentPage,
        totalPages: paginationData.totalPages || 1,
        limit: paginationData.limit || itemsPerPage
      })
    } catch (error) {
      console.error('Failed to load commissions:', error)
      setCommissions([])
      // Also retry fetching total if it failed or to keep in sync
      fetchCurrentMonthTotal()
    } finally {
      setIsLoading(false)
    }
  }

  const fetchCurrentMonthTotal = async () => {
    try {
      const currentMonth = new Date().toISOString().slice(0, 7) // "YYYY-MM"

      // Fetch both types to calculate total
      const [hotelData, experienceData] = await Promise.all([
        commissionService.getAgentCommission(1, 100),
        commissionService.getExperienceCommission(1, 100)
      ])

      const hotelCommissions = hotelData?.data?.data || []
      const experienceCommissions = experienceData?.data?.data || []

      // Filter and calculate stats for current month based on selected type
      const targetCommissions = commissionType === 'hotel' ? hotelCommissions : experienceCommissions
      const monthData = targetCommissions.find(item => item.commissionMonth === currentMonth)

      if (monthData) {
        setCurrentMonthStats({
          totalBookingValue: Number(monthData.totalBookingValue) || Number(monthData.bookingValue) || 0,
          totalBaseAmount: Number(monthData.totalBaseAmount) || Number(monthData.baseAmount) || 0,
          totalCommission: Number(monthData.totalCommissionForMonth) || 0,
          paidCommission: Number(monthData.totalPayouts) || 0,
          pendingCommission: Number(monthData.remainingBalance) || 0
        })
      } else {
        setCurrentMonthStats({
          totalBookingValue: 0,
          totalBaseAmount: 0,
          totalCommission: 0,
          paidCommission: 0,
          pendingCommission: 0
        })
      }
    } catch (error) {
      console.error('Failed to fetch current month stats:', error)
    }
  }

  const handleLogout = () => {
    travelAgentService.logout()
    navigate('/login')
  }

  // Calculate summary statistics


  const summaryStats = currentMonthStats

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0)
  }

  const formatMonth = (monthString) => {
    if (!monthString) return 'N/A'
    try {
      const [year, month] = monthString.split('-')
      const date = new Date(year, month - 1)
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' })
    } catch (error) {
      return monthString
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const handleViewPayouts = (commission) => {
    setSelectedCommission(commission)
    setShowPayoutsModal(true)
  }

  const handleViewBookings = (commission) => {
    setSelectedCommission(commission)
    if (commissionType === 'experience') {
      setShowExperienceBookingsModal(true)
    } else {
      setShowBookingsModal(true)
    }
  }
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const getStatusBadge = (status) => {
    const statusConfig = {
      paid: { color: 'bg-green-100 text-green-800', label: 'Paid' },
      partial: { color: 'bg-amber-100 text-amber-800', label: 'Partial' },
      pending: { color: 'bg-slate-100 text-slate-800', label: 'Pending' }
    }

    const config = statusConfig[status] || statusConfig.pending
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    )
  }

  const renderHotelCommissionTable = () => (
    <>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Period
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Booking Value
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Base Value
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Default Commission
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Markup commission
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Total Commission
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Paid/Remaining
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {filteredData.map((item) => (
              <tr key={item._id} className="hover:bg-slate-50 transition-colors duration-150">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-slate-900">
                    {formatMonth(item.commissionMonth)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <div className="text-sm text-slate-900 font-medium">
                      {formatCurrency(item.totalBookingValue || item.bookingValue)}
                    </div>
                    <button
                      onClick={() => handleViewBookings(item)}
                      className="text-blue-600 hover:text-blue-800 transition-colors"
                      title="View booking details"
                    >
                      <FileText className="w-4 h-4" />
                    </button>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-slate-900 font-medium">
                    {formatCurrency(item.totalBaseAmount || item.baseAmount)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-semibold text-green-600">
                    {formatCurrency(item.totalDefaultCommission)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-semibold text-green-600">
                    {formatCurrency(item.totalAdditionalCommission)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-semibold text-green-600">
                    {formatCurrency(item.totalCommissionForMonth)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-slate-600">
                    <div className="font-medium text-emerald-600">
                      {formatCurrency(item.totalPayouts)} paid
                    </div>
                    <div className="text-amber-600">
                      {formatCurrency(item.remainingBalance)} remaining
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getStatusBadge(item.payoutStatus || item.status || 'pending')}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => handleViewPayouts(item)}
                    className="flex items-center px-3 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors duration-200"
                  >
                    Payouts
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )

  const renderExperienceCommissionTable = () => (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-slate-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
              Period
            </th>

            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
              Booking Value
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
              Base Amount
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
              Commission Rate
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
              Commission
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
              Paid
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
              Remaining
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
              Status
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200">
          {filteredData.map((item) => (
            <tr key={item._id} className="hover:bg-slate-50 transition-colors duration-150">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-slate-900">
                  {formatMonth(item.commissionMonth)}
                </div>
              </td>

              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center gap-2">
                  <div className="text-sm text-slate-900 font-medium">
                    {formatCurrency(item.totalBookingValue || item.bookingValue)}
                  </div>
                  <button
                    onClick={() => handleViewBookings(item)}
                    className="text-blue-600 hover:text-blue-800 transition-colors"
                    title="View booking breakdown"
                  >
                    <FileText className="w-4 h-4" />
                  </button>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-slate-900 font-medium">
                  {formatCurrency(item.totalBaseAmount || item.baseAmount)}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-semibold text-green-600">
                  {item.allocatedCommissionRate?.toFixed(2)}%
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-semibold text-green-600">
                  {formatCurrency(item.totalCommissionForMonth)}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-emerald-600">
                  {formatCurrency(item.totalPayouts)}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-amber-600">
                  {formatCurrency(item.remainingBalance)}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {getStatusBadge(item.payoutStatus || item.status || 'pending')}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <button
                  onClick={() => handleViewPayouts(item)}
                  className="flex items-center px-3 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors duration-200"
                >
                  Payouts
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )

  if (isLoading && currentPage === 1) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
          <LoadingSpinner size="xl" />
        </div>
      </MainLayout>
    )
  }

  return (
    <MainLayout>

      {/* Hero */}
      <section className="relative h-96 bg-black overflow-hidden">
        <div className='h-12'></div>
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-55"
          style={{ backgroundImage: `url("${ACCOUNT_PAGES_HERO_IMAGE_URL}")` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-black/10 to-black/30"></div>
        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="flex items-center space-x-3 mb-2">
              <div className="w-2 h-2 bg-slate-400 rounded-full animate-pulse"></div>
              <p className="text-sm text-slate-300 font-medium tracking-wider uppercase">My Commissions</p>
            </div>
            <h2 className="text-4xl sm:text-5xl typography-hero-title-on-dark font-bold text-white mb-3">
              Commission History
            </h2>
            <p className="text-slate-300 text-lg max-w-2xl mb-6">
              Track your earnings and commission payments across all bookings
            </p>
            <div className="flex items-center space-x-4">
              <span className="text-slate-300 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full border border-white/10 text-sm font-medium">
                {pagination.totalRecords} commission periods total
              </span>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Info Box */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-200 p-4 mb-8">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-sm font-semibold text-blue-600">ℹ</span>
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-900 mb-1">How Commission is Calculated</p>
              <p className="text-sm text-blue-800">
                Your commission is calculated based on the <strong>Base Amount</strong> of each booking multiplied by your allocated commission rate. The Base Amount represents the core room/accommodation value before any additional fees, taxes or markups.
              </p>
            </div>
          </div>
        </div>

        {/* Commission Type Toggle */}
        <div className="flex flex-col sm:flex-row justify-end items-center mb-6 gap-4">
          <div className="bg-white px-4 py-2 rounded-xl shadow-sm border border-slate-200 flex items-center gap-2">
            <span className="text-sm font-medium text-slate-600">Total {commissionType === 'hotel' ? 'Hotel' : 'Experience'} Commission (This Month):</span>
            <span className="text-lg font-bold text-green-600">{formatCurrency(currentMonthStats.totalCommission)}</span>
          </div>

          <div className="bg-slate-100 p-1 rounded-xl inline-flex shadow-inner">
            <button
              onClick={() => {
                if (commissionType !== 'hotel') {
                  setCommissionType('hotel')
                  setCurrentPage(1)
                }
              }}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 flex items-center gap-2 ${commissionType === 'hotel'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200/50'
                }`}
            >
              <Building className="w-4 h-4" />
              Hotel Commissions
            </button>
            <button
              onClick={() => {
                if (commissionType !== 'experience') {
                  setCommissionType('experience')
                  setCurrentPage(1)
                }
              }}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 flex items-center gap-2 ${commissionType === 'experience'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200/50'
                }`}
            >
              <Ticket className="w-4 h-4" />
              Experience Commissions
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-medium mb-1">Booking Value (This Month)</p>
                <p className="text-2xl font-bold text-slate-900">{formatCurrency(summaryStats.totalBookingValue)}</p>
              </div>
              <div className="p-3 bg-blue-50 rounded-xl">
                <TrendingUp className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-medium mb-1">Base Amount (This Month)</p>
                <p className="text-2xl font-bold text-slate-900">{formatCurrency(summaryStats.totalBaseAmount)}</p>
              </div>
              <div className="p-3 bg-blue-50 rounded-xl">
                <TrendingUp className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-medium mb-1">Total Commission (This Month)</p>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(summaryStats.totalCommission)}</p>
              </div>
              <div className="p-3 bg-green-50 rounded-xl">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-medium mb-1">Paid Commission (This Month)</p>
                <p className="text-2xl font-bold text-emerald-600">{formatCurrency(summaryStats.paidCommission)}</p>
              </div>
              <div className="p-3 bg-emerald-50 rounded-xl">
                <Wallet className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-medium mb-1">Pending Commission (This Month)</p>
                <p className="text-2xl font-bold text-amber-600">{formatCurrency(summaryStats.pendingCommission)}</p>
              </div>
              <div className="p-3 bg-amber-50 rounded-xl">
                <ClockIcon className="h-6 w-6 text-amber-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">
                {commissionType === 'hotel' ? 'Hotel Commission History' : 'Experience Commission History'}
              </h3>
              <p className="text-slate-600 text-sm mt-1">
                {commissionType === 'hotel' ? 'Your commission earnings by month' : 'Your commission earnings from experiences'}
              </p>
            </div>
          </div>

          {filteredData.length === 0 ? (
            <div className="text-center py-12">
              <DollarSign className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 text-lg">
                No commission records found
              </p>
              <p className="text-slate-400 text-sm mt-1">
                Your commission history will appear here
              </p>
            </div>
          ) : (
            <>
              {commissionType === 'hotel' ? renderHotelCommissionTable() : renderExperienceCommissionTable()}

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
                  <div className="text-sm text-slate-500">
                    Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, pagination.totalRecords)} of{' '}
                    {pagination.totalRecords} entries
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-3 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      Previous
                    </button>
                    <span className="px-3 py-2 text-sm font-medium text-slate-600">
                      Page {currentPage} of {pagination.totalPages}
                    </span>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === pagination.totalPages}
                      className="px-3 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Payouts Modal */}
      {showPayoutsModal && selectedCommission && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
          <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[95vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">
                  Payout Details - {formatMonth(selectedCommission.commissionMonth)}
                </h3>
                <p className="text-slate-600 text-sm mt-1">
                  Total Commission: {formatCurrency(selectedCommission.totalCommissionForMonth)} •
                  Paid: {formatCurrency(selectedCommission.totalPayouts)} •
                  Remaining: {formatCurrency(selectedCommission.remainingBalance)}
                </p>
              </div>
              <button
                onClick={() => setShowPayoutsModal(false)}
                className="p-2 text-slate-400 hover:text-slate-600 transition-colors duration-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="p-6">
                {selectedCommission.payouts && selectedCommission.payouts.length > 0 ? (
                  <div className="space-y-4">
                    {selectedCommission.payouts.map((payout, index) => (
                      <div key={payout._id} className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                              <Receipt className="h-4 w-4 text-green-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-slate-900">
                                Payout #{index + 1}
                              </p>
                              <p className="text-sm text-slate-500">
                                {formatDate(payout.payoutDate)}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-green-600">
                              {formatCurrency(payout.payoutValue)}
                            </p>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              {payout.status}
                            </span>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-slate-600 font-medium mb-1">Processed By</p>
                            <p className="text-slate-900">{payout.updatedByName}</p>
                            <p className="text-slate-500 text-xs">{payout.updatedByEmail}</p>
                          </div>
                          <div>
                            <p className="text-slate-600 font-medium mb-1">Notes</p>
                            <p className="text-slate-900">{payout.notes || 'No notes provided'}</p>
                          </div>
                        </div>

                        <div className="mt-3 pt-3 border-t border-slate-200">
                          <p className="text-xs text-slate-500">
                            Processed on {formatDate(payout.createdAt)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Receipt className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500 text-lg">No payouts found</p>
                    <p className="text-slate-400 text-sm mt-1">
                      No payout records available for this commission period
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="px-6 py-4 border-t border-slate-200 bg-slate-50">
              <div className="flex justify-end">
                <button
                  onClick={() => setShowPayoutsModal(false)}
                  className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors duration-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}



      {/* Bookings Modal */}
      {showBookingsModal && selectedCommission && (
        <div className="fixed inset-1 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
          <div className="bg-white rounded-2xl shadow-xl max-w-6xl w-full max-h-[95vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">
                  Booking Details - {formatMonth(selectedCommission.commissionMonth)}
                </h3>
                <p className="text-slate-600 text-sm mt-1">
                  Total Booking Value: {formatCurrency(selectedCommission.totalBookingValue)} •
                  {selectedCommission.bookings?.length || 0} Booking(s)
                </p>
              </div>
              <button
                onClick={() => setShowBookingsModal(false)}
                className="p-2 text-slate-400 hover:text-slate-600 transition-colors duration-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="p-6">
                {selectedCommission.bookings && selectedCommission.bookings.length > 0 ? (
                  <div className="space-y-4">
                    {selectedCommission.bookings.map((booking, index) => (
                      <div
                        key={booking._id || index}
                        className={`rounded-xl p-6 border transition-all duration-300 ${booking.bookingReferenceType === 'OFF_MARKET_STAY'
                            ? 'bg-gradient-to-br from-amber-50/80 to-orange-50/50 border-amber-200 shadow-md ring-1 ring-amber-200/50'
                            : 'bg-slate-50 border-slate-200'
                          }`}
                      >
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                              <Receipt className="h-5 w-5 text-blue-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-slate-900 text-lg">
                                Booking #{index + 1}
                              </p>
                              <p className="text-sm text-slate-500">
                                Confirmation: {booking.confirmationId}
                              </p>
                              {booking.bookingReferenceType === 'OFF_MARKET_STAY' && (
                                <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 mt-2 border border-amber-200">
                                  <Star className="w-3 h-3 mr-1 fill-amber-500 text-amber-500" />
                                  Off-Market Stay
                                </div>
                              )}
                              {(booking.hotelName || booking.experienceName) && (
                                <p className="text-sm text-slate-600 mt-1">
                                  {booking.hotelName ? `Hotel: ${booking.hotelName}` : `Experience: ${booking.experienceName}`}
                                </p>
                              )}
                              {booking.hotelName ? (() => {
                                const stay = getHotelStaySourcePresentation(booking)
                                return (
                                  <div className="flex flex-wrap items-center gap-2 mt-2">
                                    <span
                                      className={`inline-flex px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide border ${stay.variant === 'offMarket'
                                          ? 'bg-amber-50 text-amber-900 border-amber-200'
                                          : 'bg-slate-100 text-slate-600 border-slate-200'
                                        }`}
                                    >
                                      {stay.variant === 'offMarket' ? 'Off-market' : 'GDS hotel'}
                                    </span>
                                    {stay.detailText ? (
                                      <span className="text-xs text-slate-600 font-medium">{stay.detailText}</span>
                                    ) : null}
                                  </div>
                                )
                              })() : null}
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-xl font-bold text-blue-600">
                              {formatCurrency(booking.bookingValue)}
                            </p>
                            <p className="text-sm text-slate-500">
                              {formatDate(booking.bookingDate)}
                            </p>
                          </div>
                        </div>

                        {/* Booking Details Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                          <div className="bg-white rounded-lg p-3 border border-slate-200">
                            <p className="text-slate-600 font-medium mb-1 flex items-center">
                              <CreditCard className="h-4 w-4 mr-2" />
                              Booking Value
                            </p>
                            <p className="text-slate-900 font-semibold">
                              {formatCurrency(booking.bookingValue)}
                            </p>
                          </div>

                          <div className="bg-white rounded-lg p-3 border border-slate-200">
                            <p className="text-slate-600 font-medium mb-1 flex items-center">
                              <CreditCard className="h-4 w-4 mr-2" />
                              Base Amount
                            </p>
                            <p className="text-slate-900 font-semibold">
                              {formatCurrency(booking.baseAmount)}
                            </p>
                          </div>

                          <div className="bg-white rounded-lg p-3 border border-slate-200">
                            <p className="text-slate-600 font-medium mb-1 flex items-center">
                              <Calendar className="h-4 w-4 mr-2" />
                              Booking Date
                            </p>
                            <p className="text-slate-900">
                              {formatDate(booking.bookingDate)}
                            </p>
                          </div>

                          <div className="bg-white rounded-lg p-3 border border-slate-200">
                            <p className="text-slate-600 font-medium mb-1 flex items-center">
                              {commissionType === 'hotel' ? <Building className="h-4 w-4 mr-2" /> : <Ticket className="h-4 w-4 mr-2" />}
                              {commissionType === 'hotel' ? 'Hotel Name' : 'Experience Name'}
                            </p>
                            <p className="text-slate-900">
                              {booking.hotelName || booking.experienceName || 'Not specified'}
                            </p>
                          </div>

                          <div className="bg-white rounded-lg p-3 border border-slate-200">
                            <p className="text-slate-600 font-medium mb-1 flex items-center">
                              <FileText className="h-4 w-4 mr-2" />
                              Confirmation ID
                            </p>
                            <p className="text-slate-900 font-mono text-sm">
                              {booking.confirmationId}
                            </p>
                          </div>
                        </div>

                        {/* Additional Information */}
                        {(booking.hotelId || booking.hotelName || booking.experienceId || booking.experienceName) && (
                          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            {(booking.hotelId || booking.experienceId) && (
                              <div className="bg-white rounded-lg p-3 border border-slate-200">
                                <p className="text-slate-600 font-medium mb-1 flex items-center">
                                  <Key className="h-4 w-4 mr-2" />
                                  {booking.hotelId ? 'Hotel ID' : 'Experience ID'}
                                </p>
                                <p className="text-slate-900 font-mono text-sm">
                                  {booking.hotelId || booking.experienceId}
                                </p>
                              </div>
                            )}
                            {(booking.hotelName || booking.experienceName) && (booking.hotelId || booking.experienceId) && (
                              <div className="bg-white rounded-lg p-3 border border-slate-200">
                                <p className="text-slate-600 font-medium mb-1 flex items-center">
                                  <MapPin className="h-4 w-4 mr-2" />
                                  {booking.hotelName ? 'Hotel Information' : 'Experience Information'}
                                </p>
                                <p className="text-slate-900 text-sm">
                                  {booking.hotelName || booking.experienceName} (ID: {booking.hotelId || booking.experienceId})
                                </p>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Commission Calculation Breakdown */}
                        {(() => {
                          const isOffMarket = booking.bookingReferenceType === 'OFF_MARKET_STAY';
                          if (isOffMarket) {
                            const defaultBasePct = booking.offMarketCommission?.defaultBasePct ?? booking.defaultCommissionPct ?? 6;
                            const specialAddedPct = booking.offMarketCommission?.specialAddedPct ?? booking.additionalPercentage ?? 6;
                            const totalPct = booking.offMarketCommission?.totalPct ?? booking.markupPercentage ?? 12;

                            const defaultCommissionAmt = booking.defaultCommission ?? (booking.baseAmount * (defaultBasePct / 100));
                            const specialCommissionAmt = booking.additionalCommission ?? (booking.baseAmount * (specialAddedPct / 100));
                            const totalCommissionAmt = booking.commissionAmount ?? (defaultCommissionAmt + specialCommissionAmt);

                            return (
                              <div className="mt-4 pt-4 border-t border-amber-200 space-y-3">
                                <div className="flex items-center justify-between text-sm">
                                  <span className="text-slate-600 font-medium">Default Commission:</span>
                                  <div className="text-right flex items-center justify-end gap-2">
                                    <span className="text-slate-500 text-xs">
                                      ({formatCurrency(booking.baseAmount)} × {defaultBasePct}%)
                                    </span>
                                    <span className="text-slate-900 font-semibold">
                                      {formatCurrency(defaultCommissionAmt)}
                                    </span>
                                  </div>
                                </div>
                                <div className="flex items-center justify-between text-sm">
                                  <span className="text-amber-700 font-semibold flex items-center">
                                    <Star className="w-3.5 h-3.5 mr-1.5 fill-amber-500 text-amber-500" />
                                    Special Commission:
                                  </span>
                                  <div className="text-right flex items-center justify-end gap-2">
                                    <span className="text-slate-500 text-xs">
                                      ({formatCurrency(booking.baseAmount)} × {specialAddedPct}%)
                                    </span>
                                    <span className="text-amber-700 font-bold">
                                      {formatCurrency(specialCommissionAmt)}
                                    </span>
                                  </div>
                                </div>
                                <div className="pt-3 border-t border-dashed border-amber-200 flex items-center justify-between text-sm">
                                  <span className="text-slate-900 font-bold">Total Commission:</span>
                                  <div className="text-right flex items-center justify-end gap-2">
                                    <span className="text-slate-500 text-xs font-medium">
                                      ({formatCurrency(booking.baseAmount)} × {totalPct}%)
                                    </span>
                                    <span className="text-green-600 font-extrabold text-base">
                                      {formatCurrency(totalCommissionAmt)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          } else {
                            // HOTEL_STAY / GDS booking
                            const defaultPct = booking.defaultCommissionPct ?? 10;
                            const markupPct = booking.additionalPercentage ?? 0;
                            const totalPct = booking.markupPercentage ?? 10;

                            const defaultCommissionAmt = booking.defaultCommission ?? (booking.baseAmount * (defaultPct / 100));
                            const markupCommissionAmt = booking.additionalCommission ?? (booking.baseAmount * (markupPct / 100));
                            const totalCommissionAmt = booking.commissionAmount ?? (defaultCommissionAmt + markupCommissionAmt);

                            return (
                              <div className="mt-4 pt-4 border-t border-slate-200 space-y-3">
                                <div className="flex items-center justify-between text-sm">
                                  <span className="text-slate-600">Default Commission:</span>
                                  <div className="text-right flex items-center justify-end gap-2">
                                    <span className="text-slate-500 text-xs">
                                      ({formatCurrency(booking.baseAmount)} × {defaultPct}%)
                                    </span>
                                    <span className="text-slate-900 font-semibold">
                                      {formatCurrency(defaultCommissionAmt)}
                                    </span>
                                  </div>
                                </div>
                                {markupPct > 0 && (
                                  <div className="flex items-center justify-between text-sm">
                                    <span className="text-slate-600 font-medium flex items-center">
                                      <TrendingUp className="h-4 w-4 mr-1.5 text-blue-500" />
                                      Markup Commission:
                                    </span>
                                    <div className="text-right flex items-center justify-end gap-2">
                                      <span className="text-slate-500 text-xs">
                                        ({formatCurrency(booking.baseAmount)} × {markupPct}%)
                                      </span>
                                      <span className="text-slate-900 font-semibold">
                                        {formatCurrency(markupCommissionAmt)}
                                      </span>
                                    </div>
                                  </div>
                                )}
                                <div className="pt-3 border-t border-dashed border-slate-200 flex items-center justify-between text-sm">
                                  <span className="text-slate-900 font-bold">Total Commission:</span>
                                  <div className="text-right flex items-center justify-end gap-2">
                                    <span className="text-slate-500 text-xs font-medium">
                                      ({formatCurrency(booking.baseAmount)} × {totalPct}%)
                                    </span>
                                    <span className="text-green-600 font-bold text-base">
                                      {formatCurrency(totalCommissionAmt)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                        })()}
                      </div>
                    ))}

                    {/* Summary Card */}
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                        <div>
                          <p className="text-sm text-blue-600 font-medium mb-1">Total Bookings</p>
                          <p className="text-2xl font-bold text-blue-900">{selectedCommission.bookings.length}</p>
                        </div>
                        <div>
                          <p className="text-sm text-blue-600 font-medium mb-1">Total Booking Value</p>
                          <p className="text-2xl font-bold text-blue-900">
                            {formatCurrency(selectedCommission.totalBookingValue)}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-blue-600 font-medium mb-1">Commission Earned</p>
                          <p className="text-2xl font-bold text-green-600">
                            {formatCurrency(selectedCommission.totalCommissionForMonth)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <FileText className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500 text-lg">No booking details available</p>
                    <p className="text-slate-400 text-sm mt-2">
                      Booking information for this commission period is not available
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="px-6 py-4 border-t border-slate-200 bg-slate-50">
              <div className="flex justify-between items-center">
                <div className="text-sm text-slate-600">
                  {selectedCommission.bookings?.length || 0} booking(s) found
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowBookingsModal(false)}
                    className="px-6 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors duration-200"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Experience Bookings Modal */}
      <ExperienceCommissionBookingsModal
        isOpen={showExperienceBookingsModal}
        onClose={() => setShowExperienceBookingsModal(false)}
        commission={selectedCommission}
        formatCurrency={formatCurrency}
        formatDate={formatDate}
        formatMonth={formatMonth}
      />
    </MainLayout>
  )
}


export default UserBookingPage